import { IconButton } from '@synoptic/ui-kit/button/icon-button.js';
import {
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuRoot,
  DropdownMenuTrigger,
} from '@synoptic/ui-kit/dropdown-menu/dropdown-menu.js';
import { ArrowEmailForwardIcon } from '@synoptic/ui-kit/icons/react/arrow-email-forward.tsx';
import { MoreHorizIcon } from '@synoptic/ui-kit/icons/react/more-horiz.js';
import { StarFilledIcon } from '@synoptic/ui-kit/icons/react/star-filled.js';
import { StarIcon } from '@synoptic/ui-kit/icons/react/star.js';
import { UserPlusIcon } from '@synoptic/ui-kit/icons/react/user-plus.tsx';
import { UserXmarkIcon } from '@synoptic/ui-kit/icons/react/user-xmark.js';
import { useIsTablet } from '@synoptic/ui-kit/responsive/hooks.ts';
import { useToast } from '@synoptic/ui-kit/toast/toast-provider.js';
import { FC } from 'react';
import { useAuthGuard } from '@/components/auth-guard/auth-guard-provider';
import {
  useFollow,
  useUnfollow,
} from '@/components/toggle-follow/use-toggle-follow.ts';
import { paths } from '@/routes-utils/paths.ts';
import { useAuthInfo } from '@/routes/_app/use-auth-info';
import { copyUrlToClipboard } from '@/utils/copy-url-to-clipboard.ts';
import { useToggleHighlight } from './use-toggle-highlight';

export interface ActionsMenuProps {
  userId: string;
  username: string;
  following: boolean;
  isHighlighted: boolean;
}

export const ProfileActionsMenu: FC<ActionsMenuProps> = ({
  userId,
  username,
  following,
  isHighlighted,
}) => {
  const isTablet = useIsTablet();
  const toast = useToast();
  const authGuard = useAuthGuard();

  const [follow] = useFollow(userId);
  const [unfollow] = useUnfollow(userId);

  const { toggleHighlight } = useToggleHighlight();

  const toggleFollowUser = () => {
    if (authGuard()) return;

    if (!following) follow();
    else unfollow();
  };

  const isAdmin = useAuthInfo().isAdmin;

  const actions = [
    <DropdownMenuItem
      key="follow"
      icon={!following ? <UserPlusIcon /> : <UserXmarkIcon />}
      onSelect={toggleFollowUser}
    >
      {!following ? 'Follow' : 'Unfollow'}
    </DropdownMenuItem>,
    <DropdownMenuItem
      key="copy-link"
      icon={<ArrowEmailForwardIcon />}
      onSelect={() => {
        copyUrlToClipboard(paths.makeUser(username));
        toast.info({ title: 'Link copied to clipboard' });
      }}
    >
      Share this profile
    </DropdownMenuItem>,
  ];

  if (isAdmin) {
    actions.push(
      <DropdownMenuItem
        key="highlight"
        icon={isHighlighted ? <StarFilledIcon /> : <StarIcon />}
        onSelect={() => toggleHighlight(userId, isHighlighted)}
      >
        {isHighlighted ? 'Unhighlight' : 'Highlight'}
      </DropdownMenuItem>,
    );
  }

  return (
    <DropdownMenuRoot>
      <DropdownMenuTrigger>
        <IconButton
          size={isTablet ? 'medium' : 'small'}
          variant="ghost"
          aria-label="Profile actions menu"
        >
          <MoreHorizIcon style={{ height: 20, width: 20 }} />
        </IconButton>
      </DropdownMenuTrigger>
      <DropdownMenuContent>{actions}</DropdownMenuContent>
    </DropdownMenuRoot>
  );
};
